import countries from 'data/countries';
import { getUtms } from 'utils/analytics';

import GlobalService from './GlobalService';

export const LEAD_SOURCES = {
  OTHER: 'Other',
  ELIGIBILITY_SURVEY: 'EligibilitySurvey',
  TYPEFORM: 'Typeform',
  LEAD_CAPTURE_SERVICE: 'LeadCaptureService',
  DIRECT_BOOKING: 'DirectBooking',
  WAITINGLIST: 'WaitingList',
  LEAD_GEN: 'Lead Gen',
};
const locale = countries[process.env.GATSBY_COUNTRY_CODE].lang;

class LeadService extends GlobalService {
  constructor() {
    super();
    this.data = {};
  }

  /**
   * Preparing lead payload
   * @param {Object} userDetails
   * @param {{brand: String}} additionalData - can contain brand
   * @param {String} leadSource
   * @returns {Object}
   */
  prepareLeadPayload(userDetails, additionalData, leadSource = '') {
    if (!this.data) throw new Error({ data: null, message: 'Failed to retrieve lead token!' });

    const { firstName, lastName, email, phoneNumber, assessment, additionalInfo, marketingConsent, city } = userDetails;
    const trackingInfo = getUtms();

    const leadPayload = {
      lead: {
        contact: {
          name: {
            first: firstName,
            last: lastName,
            full: `${firstName} ${lastName}`,
          },
          address: {
            city: city,
            countryCode: countries[process.env.GATSBY_COUNTRY_CODE].shortName,
          },
          email,
          phone: `+${phoneNumber}`,
        },
        locale,
        lead_source: leadSource,
        country_of_service: countries[process.env.GATSBY_COUNTRY_CODE].shortName,
        tracking: { ...trackingInfo },
        ...(additionalData?.brand ? { brand: additionalData.brand } : {}),
        ...(additionalInfo ? { additionalInfo } : {}),
      },
      consent: {
        consent_to_privacy_policy: true,
        consent_to_data_processing: true,
        email_opt_in_step_one: marketingConsent,
      },
      assessment: assessment || {},
    };

    return {
      leadPayload,
      requestInfo: this.data,
    };
  }

  /**
   * Sending prepared lead details to the lead capture API endpoint.
   *
   * @property {Object} formData - see prepareLeadPayload method above
   * @property {{ brand: String }} [additionalData = {}]
   * @property {String} [leadSource = LEAD_SOURCES.LEAD_GEN] - see LEAD_SOURCES at the top of this file
   *
   * @returns {{masterId: string, uuid: string}}
   */
  async sendLeadData({ formData, additionalData = {}, leadSource = LEAD_SOURCES.LEAD_GEN }) {
    /**
     * Make sure this sendLeadData method is not called within 5 seconds of calling getLeadToken.
     *
     * https://smileco.atlassian.net/wiki/spaces/MW/pages/516718741/Lead+capture+API
     */
    await new Promise(resolve => {
      if (!this.data.timeGetLeadTokenWasCalled) return resolve(true);

      const timeNow = new Date().getTime();
      const timeDifference = timeNow - this.data.timeGetLeadTokenWasCalled;

      if (timeDifference > 5000) return resolve(true);

      setTimeout(() => resolve(true), 5100 - timeDifference);
    });

    try {
      const { requestInfo, leadPayload } = this.prepareLeadPayload(formData, additionalData, leadSource);

      const response = await this.axios.put(`${requestInfo?.leadUrl}`, JSON.stringify(leadPayload), {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': requestInfo?.token,
        },
      });
      const { data, status } = response;

      if (status !== 204 && status !== 200)
        throw new Error({ data: null, error: { message: data.error, status: status } });

      return data;
    } catch (error) {
      return this.errorHandler(error);
    }
  }

  /**
   * Retrieving lead token from the booking API. We use the token for lead capture.
   * @param {Object} utmQueries Query parameters as object
   */
  async getLeadToken(utmQueries = {}) {
    // TODO: save created token in the https cookie
    //       before geting the token form the API,
    //       check if the token exists in the cookie
    const path = this.getEndpointUrl({ path: 'leads/' });

    try {
      const response = await this.axios.post(
        path,
        {},
        {
          headers: {},
          params: { ...utmQueries },
          withCredentials: true,
          xsrfCookieName: 'X-CSRF-Token',
        },
      );

      const { data, status } = response;

      if (status !== 201) throw new Error({ data: null, error: { message: data.error, status: status } });

      const leadUrl = data?._links['booking:lead']?.href;
      const token = data['X-CSRF-Token'];
      const leadId = data?.masterId;

      /**
       * We need to store the time in milliseconds to ensure sendLeadData is not
       * called within 5 seconds of our call to this getLeadToken method.
       *
       * https://smileco.atlassian.net/wiki/spaces/MW/pages/516718741/Lead+capture+API
       */
      const timeGetLeadTokenWasCalled = new Date().getTime();

      this.data = {
        leadUrl,
        token,
        leadId,
        timeGetLeadTokenWasCalled,
      };
    } catch (error) {
      return this.errorHandler(error);
    }
  }

  async updateWaitlistLeadStatus(salesforceLeadId, locationCode) {
    try {
      const path = this.getEndpointUrl({ path: `/leads/status/${salesforceLeadId}` });
      const { status, data } = await this.axios.put(
        path,
        JSON.stringify({
          lead_status: LEAD_SOURCES.WAITINGLIST,
          location: locationCode,
        }),
      );

      if (status !== 204 && status !== 200)
        throw new Error({ data: null, error: { message: data.error, status: status } });

      return path;
    } catch (error) {
      return this.errorHandler(error);
    }
  }

  errorHandler(error) {
    if (!error?.response) return error;

    const { data, status } = error.response;

    /**
     * We need to extract the existing lead Id from the error message in our
     * response when a duplicate lead error is returned.
     */
    if (status === 409) {
      const leadAlreadyExistsId = data.message.split('/ ')[1];
      return {
        masterId: leadAlreadyExistsId,
      };
    }

    return {
      data: null,
      error: data,
    };
  }
}

const leadService = new LeadService();

export default leadService;
